import 'keen-slider/keen-slider.min.css'

import React, {useCallback, useMemo, useState} from 'react'
import {useKeenSlider} from 'keen-slider/react'

import {Image} from '@vio/atlas/Image'
import {ImageProvider} from '@vio/core/src/staticContent/types/ImageProvider'

import {CAROUSEL_LOADING} from './constant'
import {CurrentSlideIndicators} from './CurrentSlideIndicators'
import {PaginationButtons} from './PaginationButtons'
import {Slide} from './Slide'
import {
  SlideNumberWrapper,
  SliderWrapper,
  VirtualizedGalleryWrapper
} from './styles'

interface Props {
  /** RTL language */
  isRtl: boolean
  /** Array of image URLs */
  images: string[]
  /** First image to display after opening */
  initialImageIndex: number
  /** Imaging service for resizing */
  imageProvider: ImageProvider
  /** Triggered whenever the user swipes the gallery */
  onSwipe?: () => void
  /** Triggered whenever the user zooms the gallery */
  onZoom?: () => void
  /** Show Pagination buttons */
  showPaginationButtons?: boolean
  /** Is zoom on Slides enabled */
  isZoomEnabled?: boolean
}

export const VirtualizedGallery = ({
  images,
  imageProvider,
  initialImageIndex,
  isRtl,
  showPaginationButtons = false,
  isZoomEnabled = false,
  onZoom,
  onSwipe
}: Props) => {
  const [isLoaded, setLoaded] = useState(false)
  const [imageIndex, setImageIndex] = useState(initialImageIndex)
  const slides = useMemo(() => [...(images || [])], [images])

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    loop: true,
    rtl: isRtl,
    initial: initialImageIndex,
    slides: slides.length,
    slideChanged: slider => {
      const newIndex = slider.track.details.rel
      setImageIndex(newIndex)
      onSwipe?.()
    },
    created: slider => {
      setTimeout(() => {
        slider.update()
        setLoaded(true)
      }, CAROUSEL_LOADING)
    }
  })

  const shouldLoadThisImage = useCallback(
    (currentIdx: number, selectedImage: number) => {
      return [
        0,
        selectedImage,
        selectedImage + 1,
        selectedImage - 1,
        slides.length - 1
      ].includes(currentIdx)
    },
    [slides]
  )

  const goToSlide = (idx: number) => {
    instanceRef.current?.moveToIdx(idx)
  }

  return (
    <VirtualizedGalleryWrapper>
      <SliderWrapper ref={sliderRef} className="keen-slider">
        {slides.map((image, idx) => {
          return (
            <div key={image} className="keen-slider__slide">
              {shouldLoadThisImage(idx, imageIndex) && (
                <Slide isZoomEnabled={isZoomEnabled} onZoom={onZoom}>
                  <Image
                    fullWidth
                    isResponsive
                    imageProvider={imageProvider}
                    backgroundSize="contain"
                    url={image}
                  />
                </Slide>
              )}
            </div>
          )
        })}
      </SliderWrapper>

      {isLoaded && instanceRef.current && slides.length > 1 && (
        <CurrentSlideIndicators
          amount={slides.length}
          currentIndex={imageIndex}
          isRtl={isRtl}
        />
      )}

      {isLoaded && instanceRef.current && (
        <PaginationButtons
          currentIndex={imageIndex}
          totalPages={slides.length}
          goToSlide={goToSlide}
          showPaginationButtons={showPaginationButtons}
        />
      )}

      {isLoaded && instanceRef.current && (
        <SlideNumberWrapper>
          {`${imageIndex + 1}/${slides.length}`}
        </SlideNumberWrapper>
      )}
    </VirtualizedGalleryWrapper>
  )
}
